import { getBaseUrl } from './baseUrl';

export type PlatformSupportAlertSeverity = 'CRITICAL' | 'WARNING' | 'INFO';

export type PlatformSupportAlert = {
  key: string;
  type: string;
  severity: PlatformSupportAlertSeverity;
  title: string;
  message: string;
  recommendation: string;
  target: {
    type: string;
    id: string;
    name: string;
    licenseId?: string;
    organization?: { id: string; name: string | null; slug: string | null } | null;
  };
  observedAt: string;
  evidence: Record<string, unknown>;
};

export type PlatformSupportAlertList = {
  records: PlatformSupportAlert[];
  summary: { total: number; critical: number; warning: number; info: number; byType: Record<string, number> };
  filters: { page: number; pageSize: number; severity: string; type: string; search: string; accountId: string; seatProduct: string; userId: string };
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
  types: string[];
  filterOptions: {
    accounts: Array<{ id: string; name: string; type: 'UNIT' | 'ORG' }>;
    users: Array<{ id: string; name: string; accountIds: string[] }>;
    seatProducts: Array<{ code: string; label: string; accountIds: string[]; userIds: string[] }>;
  };
  thresholds: Record<string, number>;
};

export async function fetchPlatformSupportAlerts(
  token: string,
  filters: { page?: number; pageSize?: number; severity?: string; type?: string; search?: string; accountId?: string; seatProduct?: string; userId?: string } = {}
): Promise<PlatformSupportAlertList> {
  const base = await getBaseUrl();
  const query = new URLSearchParams();
  if (filters.page) query.set('page', String(filters.page));
  if (filters.pageSize) query.set('pageSize', String(filters.pageSize));
  if (filters.severity && filters.severity !== 'ALL') query.set('severity', filters.severity);
  if (filters.type && filters.type !== 'ALL') query.set('type', filters.type);
  if (filters.search) query.set('search', filters.search);
  if (filters.accountId) query.set('accountId', filters.accountId);
  if (filters.seatProduct) query.set('seatProduct', filters.seatProduct);
  if (filters.userId) query.set('userId', filters.userId);
  const response = await fetch(`${base}/api/platform-support/alerts?${query}`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store'
  });
  const payload = await response.json().catch(() => null) as PlatformSupportAlertList | { error?: string } | null;
  if (!response.ok) {
    throw new Error(payload && 'error' in payload && payload.error ? payload.error : 'Proactive support alerts could not be loaded');
  }
  return payload as PlatformSupportAlertList;
}
